/**
 * PulseMeter — the Act 4 "ghost target" meter that's BORN at 0 when
 * ignition fires.
 *
 * VISUAL
 *   ┌──────────────────────────┐
 *   │ PULSE                    │
 *   │     0 ─────────●─── 100  │  ← pink tick + "target" label at
 *   │            target 72     │     projected_pulse_target
 *   └──────────────────────────┘
 *
 * SAFE-SURFACE CONTRACT:
 *   `target` MUST be the `act4.projected_pulse_target` value from the
 *   backend (CMD-sourced). When null, the tick + label do NOT render —
 *   the meter shows a value-less bar instead of fabricating a number.
 *
 * The meter materializes (fades in + slides up) when `visible=true`.
 * It starts at value=0 — actual pulse growth happens AFTER navigation,
 * inside the Wave Control Room.
 */
import React from 'react';

const PINK = '#FF4D94';
const TEAL = '#00D4FF';

export default function PulseMeter({
  target,                 // number 0..100 | null
  current = 0,            // number 0..100
  visible = false,        // gates the materialize animation
}) {
  const hasTarget = Number.isFinite(target);
  const targetPct = hasTarget ? Math.max(0, Math.min(100, target)) : 0;
  const currentPct = Math.max(0, Math.min(100, current));

  return (
    <div
      data-testid="pulse-meter"
      data-visible={visible ? 'true' : 'false'}
      data-has-target={hasTarget ? 'true' : 'false'}
      className="rounded-xl px-4 py-3"
      style={{
        width:  300,
        background: 'rgba(15,15,15,0.92)',
        border:     `1px solid ${PINK}55`,
        backdropFilter: 'blur(8px)',
        boxShadow: `0 0 28px ${PINK}33`,
        opacity:   visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-12px)',
        transition: 'opacity 600ms ease-out 200ms, transform 600ms ease-out 200ms',
        pointerEvents: 'none',
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className="font-black uppercase"
          style={{ color: PINK, fontSize: 11, letterSpacing: '0.22em' }}
        >
          Pulse
        </span>
        <span
          className="font-black"
          data-testid="pulse-current-value"
          style={{ color: '#FFF', fontSize: 22, lineHeight: 1 }}
        >
          {currentPct}
        </span>
      </div>

      {/* Bar track */}
      <div className="relative h-2.5 rounded-full" style={{ background: 'rgba(255,255,255,0.10)' }}>
        {/* Filled portion — current pulse value */}
        <div
          data-testid="pulse-fill"
          className="absolute top-0 left-0 h-full rounded-full"
          style={{
            width: `${currentPct}%`,
            background: `linear-gradient(90deg, ${TEAL}, ${PINK})`,
            boxShadow: `0 0 12px ${PINK}88`,
            transition: 'width 800ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />

        {/* Target tick — pink vertical mark at projected pulse position.
            Hidden when target is null (safe-surface contract). */}
        {hasTarget && (
          <div
            data-testid="pulse-target-tick"
            className="absolute top-1/2 -translate-y-1/2"
            style={{
              left: `calc(${targetPct}% - 1px)`,
              width: 3,
              height: 18,
              background: PINK,
              borderRadius: 2,
              boxShadow: `0 0 8px ${PINK}, 0 0 14px ${PINK}aa`,
            }}
          />
        )}
      </div>

      {/* Axis labels — 0 / 100 + target label */}
      <div className="relative mt-1.5" style={{ height: 16 }}>
        <span
          className="absolute left-0 text-[9px] font-bold"
          style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.12em' }}
        >
          0
        </span>
        <span
          className="absolute right-0 text-[9px] font-bold"
          style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.12em' }}
        >
          100
        </span>
        {hasTarget && (
          <span
            data-testid="pulse-target-label"
            className="absolute font-black"
            style={{
              left: `${targetPct}%`,
              transform: 'translateX(-50%)',
              color: PINK,
              fontSize: 10,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              textShadow: `0 0 6px ${PINK}88`,
            }}
          >
            target {targetPct}
          </span>
        )}
      </div>
    </div>
  );
}
